import React, { useEffect } from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  VStack,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
} from "@chakra-ui/react";
import useOfferStore from "../store/useOfferStore";
import {
  AddIcon,
  ViewIcon,
  EditIcon,
  DeleteIcon,
  HamburgerIcon,
  SearchIcon,
  DownloadIcon,
  CheckIcon,
} from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import offerService from "../services/offerService";
import atsService from "../modules/recruitment/atsService";
import OfferDetailsModal from "../components/OfferDetailsModal";

// Helper function to get formatted candidate name
const getCandidateName = (candidateId) => {
  if (!candidateId) return "No Candidate";

  // If full name exists, use it
  if (candidateId.name) return candidateId.name;

  // If both first and last name exist, combine them
  if (candidateId.firstName && candidateId.lastName) {
    return `${candidateId.firstName} ${candidateId.lastName}`;
  }

  // If only one of first or last name exists
  if (candidateId.firstName) return candidateId.firstName;
  if (candidateId.lastName) return candidateId.lastName;

  // If no name fields exist
  return "Unnamed Candidate";
};

// Helper function to get formatted position
const getPosition = (requisitionId) => {
  if (!requisitionId) return "No Position";

  // Try jobTitle first, then positionTitle
  return (
    requisitionId.jobTitle ||
    requisitionId.positionTitle ||
    "Position Not Specified"
  );
};

const Offers = () => {
  // Dark mode colors
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  // Zustand store hooks
  const {
    offers,
    loading,
    error,
    filters,
    selectedOffer,
    pagination,
    fetchOffers,
    setFilters,
    setSelectedOffer,
    setPage,
    sendOffer,
    withdrawOffer,
  } = useOfferStore();

  const {
    isOpen: isDetailsOpen,
    onOpen: onDetailsOpen,
    onClose: onDetailsClose,
  } = useDisclosure();

  const {
    isOpen: isApprovalOpen,
    onOpen: onApprovalOpen,
    onClose: onApprovalClose,
  } = useDisclosure();

  const [offerToSubmit, setOfferToSubmit] = React.useState(null);
  const [approvalWorkflow, setApprovalWorkflow] = React.useState({
    workflowType: "sequential",
    approvalStages: [],
  });
  const [submittingApproval, setSubmittingApproval] = React.useState(false);

  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Log the authentication status
    const token = localStorage.getItem("token");
    console.log("Authentication token present:", !!token);

    // Initial fetch
    fetchOffers();

    // Setup refresh interval
    const intervalId = setInterval(fetchOffers, 30000); // Refresh every 30 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [filters, pagination.page, fetchOffers]);

  const handleViewOffer = (offer) => {
    setSelectedOffer(offer);
    onDetailsOpen();
  };

  const handleEditOffer = (offer) => {
    navigate(`/offers/edit/${offer._id}`);
  };

  const handleSendOffer = async (offer) => {
    const result = await sendOffer(offer._id);
    if (result.success) {
      toast({
        title: "Success",
        description: "Offer sent successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to send offer",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleWithdrawOffer = async (offer) => {
    const result = await withdrawOffer(offer._id, "Withdrawn by user");
    if (result.success) {
      toast({
        title: "Success",
        description: "Offer withdrawn successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to withdraw offer",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleViewApplication = (offer) => {
    // Navigate to application details page or open modal
    navigate(`/recruitment/applications/${offer.applicationId}`);
  };

  const handleSubmitForApproval = (offer) => {
    setOfferToSubmit(offer);
    // Set default approval stages
    setApprovalWorkflow({
      workflowType: "sequential",
      approvalStages: [
        {
          stageName: "Manager Approval",
          approverRole: "Manager",
          stageOrder: 0,
        },
        {
          stageName: "HR Approval",
          approverRole: "HR Manager",
          stageOrder: 1,
        },
      ],
    });
    onApprovalOpen();
  };

  const handleConfirmSubmitApproval = async () => {
    setSubmittingApproval(true);
    try {
      const response = await offerService.submitForApproval(
        offerToSubmit._id,
        approvalWorkflow
      );

      toast({
        title: "Success",
        description: "Offer submitted for approval successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onApprovalClose();
      fetchOffers(); // Refresh offers
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to submit for approval",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmittingApproval(false);
    }
  };

  const getStatusBadge = (offer) => {
    const statusColors = {
      // Offer statuses
      draft: "gray",
      pending_approval: "yellow",
      approved: "blue",
      sent: "purple",
      pending_acceptance: "orange",
      accepted: "green",
      rejected: "red",
      expired: "red",
      withdrawn: "red",
      cancelled: "red",
      // ATS pipeline statuses
      final_interview: "blue",
      offer_pending: "orange",
      offer_accepted: "green",
      offer_rejected: "red",
      pending_offer_creation: "yellow",
    };

    const statusLabels = {
      // Offer statuses
      draft: "Draft",
      pending_approval: "Pending Approval",
      approved: "Approved",
      sent: "Sent",
      pending_acceptance: "Pending Acceptance",
      accepted: "Accepted",
      rejected: "Rejected",
      expired: "Expired",
      withdrawn: "Withdrawn",
      cancelled: "Cancelled",
      // ATS pipeline statuses
      final_interview: "Ready for Offer",
      offer_pending: "Offer Pending",
      offer_accepted: "Offer Accepted",
      offer_rejected: "Offer Rejected",
      pending_offer_creation: "Ready for Offer Creation",
    };

    // Use status directly since it's normalized in the store
    const status = offer.status;

    return (
      <Badge colorScheme={statusColors[status] || "gray"}>
        {statusLabels[status] || status}
      </Badge>
    );
  };

  const filteredOffers = offers.filter((offer) => {
    // Show all offers, but prioritize ATS-generated ones
    const matchesStatus = !filters.status || offer.status === filters.status;
    const searchTerm = (filters.search || "").toLowerCase();
    const matchesSearch =
      !filters.search ||
      (offer.candidateName || "").toLowerCase().includes(searchTerm) ||
      (offer.position || "").toLowerCase().includes(searchTerm) ||
      (offer.department || "").toLowerCase().includes(searchTerm) ||
      (offer.offerId || "").toLowerCase().includes(searchTerm);
    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="400px">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between">
          <Box>
            <Text fontSize="2xl" fontWeight="bold">
              Offer Management
            </Text>
            <Text color="gray.600">
              View and manage all offers, including those created from ATS
              applications
            </Text>
          </Box>
          <Button
            leftIcon={<ViewIcon />}
            colorScheme="blue"
            onClick={() => navigate("/recruiting/offers/approvals")}
          >
            Manage Approvals
          </Button>
        </HStack>

        {/* Filters */}
        <HStack spacing={4}>
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search offers..."
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
            />
          </InputGroup>

          <Select
            placeholder="All Status"
            maxW="200px"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="draft">Draft</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="sent">Sent</option>
            <option value="pending_acceptance">Pending Acceptance</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
            <option value="withdrawn">Withdrawn</option>
            <option value="cancelled">Cancelled</option>
            {/* ATS Pipeline Statuses */}
            <option value="offer_pending">Offer Pending</option>
            <option value="offer_accepted">Offer Accepted</option>
            <option value="offer_rejected">Offer Rejected</option>
            <option value="pending_offer_creation">
              Pending Offer Creation
            </option>
          </Select>
        </HStack>

        {/* Offers Table */}
        <Box borderWidth={1} borderRadius="lg" overflow="hidden">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Offer ID</Th>
                <Th>Candidate</Th>
                <Th>Position</Th>
                <Th>Source</Th>
                <Th>Status</Th>
                <Th>Valid Till</Th>
                <Th>Created</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredOffers.map((offer) => (
                <Tr key={offer._id}>
                  <Td fontWeight="medium">{offer.offerId}</Td>
                  <Td>{offer.candidateName}</Td>
                  <Td>
                    {offer.position !== "Position Not Specified"
                      ? offer.position
                      : "No Position"}
                  </Td>
                  <Td>
                    {offer.applicationId ? (
                      <Badge colorScheme="blue" variant="subtle">
                        ATS
                      </Badge>
                    ) : (
                      <Badge colorScheme="gray" variant="subtle">
                        Manual
                      </Badge>
                    )}
                  </Td>
                  <Td>{getStatusBadge(offer)}</Td>
                  <Td>
                    {offer.validTill
                      ? new Date(offer.validTill).toLocaleDateString()
                      : "N/A"}
                  </Td>
                  <Td>{new Date(offer.offerDate).toLocaleDateString()}</Td>
                  <Td>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<HamburgerIcon />}
                        variant="ghost"
                        size="sm"
                      />
                      <MenuList>
                        {offer.hasOffer ? (
                          // Actions for actual offers
                          <>
                            <MenuItem
                              icon={<ViewIcon />}
                              onClick={() => handleViewOffer(offer)}
                            >
                              View Details
                            </MenuItem>
                            {offer.status === "draft" && (
                              <MenuItem
                                icon={<EditIcon />}
                                onClick={() => handleEditOffer(offer)}
                              >
                                Edit
                              </MenuItem>
                            )}
                            {(offer.status === "draft" ||
                              offer.status === "pending_approval") && (
                              <MenuItem
                                icon={<CheckIcon />}
                                onClick={() => handleSubmitForApproval(offer)}
                              >
                                Submit for Approval
                              </MenuItem>
                            )}
                            {offer.status === "approved" && (
                              <MenuItem onClick={() => handleSendOffer(offer)}>
                                Send to Candidate
                              </MenuItem>
                            )}
                            {(offer.status === "sent" ||
                              offer.status === "pending_acceptance") && (
                              <MenuItem
                                onClick={() => handleWithdrawOffer(offer)}
                              >
                                Withdraw Offer
                              </MenuItem>
                            )}
                            {offer.documentUrl && (
                              <MenuItem
                                icon={<DownloadIcon />}
                                as="a"
                                href={offer.documentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Download PDF
                              </MenuItem>
                            )}
                          </>
                        ) : (
                          // Actions for applications without offers
                          <>
                            <MenuItem
                              icon={<ViewIcon />}
                              onClick={() => handleViewApplication(offer)}
                            >
                              View Application
                            </MenuItem>
                          </>
                        )}
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {filteredOffers.length === 0 && (
          <Box textAlign="center" py={10}>
            <Text fontSize="lg" color="gray.500">
              No offers found
            </Text>
          </Box>
        )}

        {/* Pagination */}
        {filteredOffers.length > 0 && (
          <HStack justify="space-between" align="center" pt={4}>
            <Text color="gray.600">
              Showing {filteredOffers.length} of {pagination.totalDocs} offers
            </Text>
            <HStack spacing={2}>
              <Button
                size="sm"
                onClick={() => setPage(pagination.prevPage)}
                isDisabled={!pagination.hasPrevPage}
              >
                Previous
              </Button>
              <Text>
                Page {pagination.page} of {pagination.totalPages}
              </Text>
              <Button
                size="sm"
                onClick={() => setPage(pagination.nextPage)}
                isDisabled={!pagination.hasNextPage}
              >
                Next
              </Button>
            </HStack>
          </HStack>
        )}
      </VStack>

      {/* Modals */}
      <OfferDetailsModal
        isOpen={isDetailsOpen}
        onClose={onDetailsClose}
        offer={selectedOffer}
      />

      {/* Approval Submission Modal */}
      <Modal isOpen={isApprovalOpen} onClose={onApprovalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Submit Offer for Approval</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontWeight="bold" mb={2}>
                  Offer Details
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Offer ID: {offerToSubmit?.offerId}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Candidate: {offerToSubmit?.candidateName}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Position: {offerToSubmit?.position}
                </Text>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={2}>
                  Workflow Type
                </Text>
                <Select
                  value={approvalWorkflow.workflowType}
                  onChange={(e) =>
                    setApprovalWorkflow({
                      ...approvalWorkflow,
                      workflowType: e.target.value,
                    })
                  }
                >
                  <option value="sequential">Sequential (One by One)</option>
                  <option value="parallel">Parallel (All at Once)</option>
                  <option value="conditional">Conditional</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {approvalWorkflow.workflowType === "sequential" &&
                    "Approvals happen one after another"}
                  {approvalWorkflow.workflowType === "parallel" &&
                    "All approvers receive request simultaneously"}
                  {approvalWorkflow.workflowType === "conditional" &&
                    "Approvals based on conditions"}
                </Text>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={2}>
                  Approval Stages
                </Text>
                <VStack align="stretch" spacing={2}>
                  {approvalWorkflow.approvalStages.map((stage, index) => (
                    <HStack
                      key={index}
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                      justify="space-between"
                    >
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="semibold">
                          Stage {index + 1}: {stage.stageName}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          Role: {stage.approverRole}
                        </Text>
                      </VStack>
                      <Badge colorScheme="blue">Required</Badge>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box fontSize="sm">
                  <Text>
                    This will create an approval workflow and notify the
                    approvers via email.
                  </Text>
                </Box>
              </Alert>

              <HStack justify="flex-end" spacing={3}>
                <Button
                  variant="ghost"
                  onClick={onApprovalClose}
                  isDisabled={submittingApproval}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleConfirmSubmitApproval}
                  isLoading={submittingApproval}
                  loadingText="Submitting..."
                >
                  Submit for Approval
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Offers;
